import React from "react";
import { FadeIn } from "../../../../components/animations/FadeIn";
import { ScaleIn } from "../../../../components/animations/ScaleIn";
import { HoverScale } from "../../../../components/animations/HoverScale";

export const FeatureHighlightSection = (): JSX.Element => {
  const stats = [
    {
      icon: "👥",
      number: "500+",
      description: "Active employees using Safora to manage their daily tasks and workflow.",
    },
    {
      icon: "📈",
      number: "120+",
      description: "Companies scaling productivity with centralized team communication.",
    },
    {
      icon: "🕒",
      number: "99.9%",
      description: "Uptime to ensure smooth and uninterrupted collaboration.",
    },
  ];

  const titles = [
    {
      icon: "💬",
      title: "Smart Communication",
      description: "Empower employees with real-time messaging and internal collaboration.",
    },
    {
      icon: "🗂️",
      title: "Task Management",
      description: "Assign, track, and deliver projects on time with role-based access.",
    },
    {
      icon: "📅",
      title: "Attendance & Leave",
      description: "Monitor attendance, approve leaves, and streamline HR operations.",
    },
  ];

  return (
    <section id="features" className="w-full max-w-[1308px] mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
      {/* Hero Text */}
      <div className="mb-16 text-center md:text-left">
        <FadeIn direction="up" delay={0.2}>
          <h1 className="text-[36px] sm:text-[48px] md:text-[64px] leading-tight font-satoshi font-bold text-gray-900 mb-6">
            Connect. Collaborate. <br className="hidden md:block" />
            Succeed with Safora.
          </h1>
        </FadeIn>
        <FadeIn direction="up" delay={0.4}>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-[600px] mx-auto md:mx-0 font-satoshi">
            All-in-one employee management platform for modern teams. From task tracking to payroll — Safora streamlines everything for your workforce.
          </p>
        </FadeIn>
        <FadeIn direction="up" delay={0.6}>
          <div className="mt-8">
            <HoverScale>
              <button className="bg-gray-900 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-satoshi font-bold hover:bg-gray-800 transition-colors">
                Get started
              </button>
            </HoverScale>
          </div>
        </FadeIn>
      </div>

      {/* Stats and Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
        {/* Stats */}
        <div className="space-y-8">
          {stats.map((stat, index) => (
            <ScaleIn key={index} delay={0.2 * index}>
              <div className="flex items-start gap-6">
                <div
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center text-2xl sm:text-3xl"
                  style={{
                    backgroundColor:
                      index === 0 ? "#4D9FFF" : index === 1 ? "#FFD572" : "#A98CFF",
                  }}
                >
                  {stat.icon}
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold font-satoshi text-gray-900 mb-1">
                    {stat.number}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 font-satoshi">
                    {stat.description}
                  </p>
                </div>
              </div>
            </ScaleIn>
          ))}
        </div>

        {/* Feature Titles */}
        <div className="space-y-8">
          {titles.map((title, index) => (
            <ScaleIn key={index} delay={0.2 * index}>
              <div className="flex items-start gap-6">
                <div
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center text-2xl sm:text-3xl"
                  style={{
                    backgroundColor:
                      index === 0 ? "#FFB7C1" : index === 1 ? "#7FE0A1" : "#72E4E9",
                  }}
                >
                  {title.icon}
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold font-satoshi text-gray-900 mb-1">
                    {title.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 font-satoshi">
                    {title.description}
                  </p>
                </div>
              </div>
            </ScaleIn>
          ))}
        </div>
      </div>
    </section>
  );
};